/**
 * Cookie/tracking consent types (RQ78378 / RQ78379).
 *
 * Enforcement boundary: the console only enables a non-essential collector
 * (today: first-party product analytics — no third-party SDK ships at all)
 * when consent resolves to permitted. These
 * tokens mirror the `/public-config` wire shape and the `consent_records`
 * DB CHECK constraints exactly — keep them in sync (no mapping layer).
 */

/** Jurisdiction policy, served by `/public-config`. Lowercase = wire/DB tokens. */
export type Jurisdiction = 'consent_required' | 'opt_out' | 'not_required'

/** Consent buckets. STRICTLY_NECESSARY is implicit (always on, never stored). */
export type ConsentCategory =
  | 'strictly_necessary'
  | 'functional'
  | 'analytics'
  | 'marketing'

/** The toggleable (non-necessary) categories, as stored. */
export interface ConsentCategories {
  analytics: boolean
  functional: boolean
  marketing: boolean
}

/** Which UI action produced a consent record (mirrors DB CHECK). */
export type SourceAction =
  | 'accept_all'
  | 'reject_nonessential'
  | 'save_preferences'
  | 'account_link'
  | 'withdraw'

/**
 * Persisted client consent state (localStorage `dw_consent`). The cookie-like
 * web storage is the synchronous source of truth for the SDK gate; a best-effort
 * server audit row mirrors it for compliance.
 */
export interface ConsentState {
  /** Policy version this decision was made under (re-consent when it changes). */
  v: string
  /** Per-category booleans (excludes strictly_necessary). */
  cats: ConsentCategories
  /** UI action that produced it. */
  src: SourceAction
  /** ISO timestamp of capture. */
  ts: string
  /** First-party device id (stable UUID, not PII-derived). */
  did: string
  /** Per-decision UUID — idempotency key for the server audit write. */
  eventId: string
}

/**
 * Consent regime is fixed to the strictest setting for ALL regions — opt-in
 * everywhere — rather than being configured per deployment. This keeps the gate
 * simple and maximally compliant; if region-specific behavior is ever needed,
 * the resolver already supports `opt_out` / `not_required` and this constant can
 * be replaced with a runtime source.
 */
export const CONSENT_JURISDICTION: Jurisdiction = 'consent_required'

/**
 * Opaque consent policy version stamped onto every stored decision. Bump this
 * (in code) when the cookie policy / vendor set changes to force all users to
 * re-consent (their stored decision becomes stale and the blocking wall returns).
 */
export const CONSENT_POLICY_VERSION = '1'

export const ALL_CATEGORIES: readonly ConsentCategory[] = [
  'strictly_necessary',
  'functional',
  'analytics',
  'marketing',
]

/** Default (pre-choice) category state: all non-essential OFF. */
export function defaultCategories(): ConsentCategories {
  return { analytics: false, functional: false, marketing: false }
}
